import { Box, Dialog, Typography } from "@material-ui/core";
import { useTranslation } from "react-i18next";
import TMoveDestination from "../../../../admin/AdminMoveEntity/types/TMoveDestination";
import TSelectedMoveTab from "../../../../admin/AdminMoveEntity/types/TMoveTab";
import SearchDestinationPanel from "../../../../../ui/components/SearchDestinationPanel/SearchDestinationPanel";
import { DEFAULT_SPACING } from "../../../../../ui/theme/dimensions";
import Property from "../../../../../rest/properties/model/Property";
import MoveEntitiesReviewPanel from "../../../../../ui/components/MoveEntitiesReviewPanel/MoveEntitiesReviewPanel";
import { formatPropertyAddress } from "../../../../../utils/formatAddress";
import MoveEntityWarningPanel from "../../../../../ui/components/MoveEntityWarningPanel/MoveEntityWarningPanel";
import MoveEntitySuccessPanel from "../../../../../ui/components/MoveEntitySuccessPanel/MoveEntitySuccessPanel";
import { IMoveActionResult } from "../../../../admin/AdminMoveEntity/services/AdminMoveEntityService";
import MoveEntityErrorPanel from "../../../../../ui/components/MoveEntityErrorPanel/MoveEntityErrorPanel";

interface IProps {
  open: boolean;
  handleOpen: () => void;
  handleSearchPressed: () => void;
  handleSetSearchString: (v: string) => void;
  setSelectedDestination: (id: string) => void;
  searchValue: string;
  isLoading: boolean | undefined;
  moved: boolean | undefined;
  destinations: TMoveDestination[];
  handleNextPressed: () => void;
  handleBackPressed: () => void;
  handleConfirmPressed: () => void;
  handleClosePressed: () => void;
  activeTab: TSelectedMoveTab;
  moveResults?: IMoveActionResult;
  selectedDestination?: string;
  properties: Property[];
}

const MovePropertiesDialog = ({
  open,
  handleOpen,
  activeTab,
  handleSetSearchString,
  setSelectedDestination,
  searchValue,
  isLoading,
  moveResults,
  destinations,
  handleBackPressed,
  handleNextPressed,
  selectedDestination,
  handleSearchPressed,
  handleConfirmPressed,
  handleClosePressed,
  properties,
}: IProps) => {
  //Hooks
  const { t } = useTranslation();

  //Strings
  const searchPlaceholder = t("movePropertyDialog.searchPlaceholder");
  const destination = destinations.filter((dest) => dest.id === selectedDestination)[0];

  const getSuccessItems = () => {
    return properties.filter((prop) => {
      return moveResults?.movedEntities.find((id) => id === prop.id) !== undefined;
    });
  };

  const getFailureItems = () => {
    return properties.filter((prop) => {
      return moveResults?.rejectedEntities.find((id) => id === prop.id) !== undefined;
    });
  };

  const getTitle = () => {
    switch (activeTab) {
      case "destination":
        return t("movePropertyDialog.destinationTitle");
      case "confirmation":
        return t("movePropertyDialog.title", { count: properties.length });
      case "warning":
        return t("movePropertyDialog.confirmationTitle");
      case "success":
        return t("movePropertyDialog.successTitle", { count: properties.length });
      case "failure":
        return t("movePropertyDialog.failureTitle", { count: properties.length });
    }
  };

  const title = getTitle();

  const renderBody = () => {
    switch (activeTab) {
      case "destination":
        return (
          <SearchDestinationPanel
            searchPlaceholder={searchPlaceholder}
            searchValue={searchValue}
            handleSearchChange={handleSetSearchString}
            handleSearchButtonClicked={handleSearchPressed}
            handleBackClicked={handleOpen}
            handleNextClicked={handleNextPressed}
            handleSelectDestination={setSelectedDestination}
            selectedDestinationId={selectedDestination}
            destinations={destinations}
            isLoading={isLoading}
          />
        );
      case "confirmation":
        return (
          <MoveEntitiesReviewPanel
            destination={destination}
            entityType={"property"}
            selectedItems={properties}
            getSelectedItemName={(item) => formatPropertyAddress(item, t)}
            handleBackPressed={handleBackPressed}
            handleNextPressed={handleNextPressed}
          />
        );
      case "warning":
        return (
          <MoveEntityWarningPanel
            entityType={"property"}
            loading={isLoading}
            destination={destination}
            selectedItems={properties}
            getSelectedItemName={(item) => `${formatPropertyAddress(item, t)}`}
            handleBackPressed={handleBackPressed}
            handleNextPressed={handleConfirmPressed}
          />
        );
      case "success":
        return (
          <MoveEntitySuccessPanel
            entityType={"property"}
            moveResults={moveResults}
            getItemName={(item) => `${formatPropertyAddress(item, t)}`}
            destination={destination}
            items={getSuccessItems()}
            rejectedItems={getFailureItems()}
            handleClosePressed={handleClosePressed}
          />
        );
      case "failure":
        return (
          <MoveEntityErrorPanel
            entityType={"procheck"}
            getItemName={(item) => `${formatPropertyAddress(item, t)}`}
            destination={destination}
            items={getFailureItems()}
            handleClosePressed={handleOpen}
          />
        );
    }
  };

  return (
    <Dialog open={open} onClose={handleOpen}>
      <Box
        display="flex"
        flexDirection="column"
        width={500}
        paddingX={DEFAULT_SPACING * 1.5}
        paddingBottom={DEFAULT_SPACING}
        paddingTop={DEFAULT_SPACING * 1.5}
      >
        <Typography
          align="center"
          style={{ textTransform: "uppercase" }}
          variant="h2"
          gutterBottom
        >
          <strong>{title}</strong>
        </Typography>
        {renderBody()}
      </Box>
    </Dialog>
  );
};

export default MovePropertiesDialog;
